import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import CompletedImg from '../../../../assets/completed.svg';
import PuzzleImg from '../../../../assets/puzzle.svg';
import Blockquote from '../../../../components/Blockquote';
import Button from '../../../../components/Button';
import Link from '../../../../components/Link';
import useModal from '../../../../hooks/useModal';
import { ModalManagementStore } from '../../../../providers/modal-manager';
import {
  PROFILE_ROUTE,
  RETIREMENT_PLANNING_ROUTE,
} from '../../../../routes/types';
import { DateFormats, formatDate } from '../../../../utils/utils';
import BeneficiariesSummaryModal from '../BeneficiariesSummaryModal';
import ReceiveFormsMailModal from '../../ReceiveFormsMailModal/ReceiveFormsMailModal';

export type SubmittedSuccessfulProps = React.PropsWithChildren<{
  spouseSelected: boolean;
  onClick?: () => void;
}>;

const SubmittedSuccessful = ({
  spouseSelected,
  onClick,
}: SubmittedSuccessfulProps) => {
  const { openModal } = useContext(ModalManagementStore);
  const [showSummaryModal, handleShowSummaryModal, handleCloseSummaryModal] =
    useModal();

  const [
    showBeneficiarySummaryModal,
    handleShowBeneficiarySummaryModal,
    handleCloseBeneficiarySummaryModal,
  ] = useModal();

  const [
    showReceiveMailModal,
    handleShowReceiveMailModal,
    handleCloseReceiveMailModal,
  ] = useModal();

  const initialDate = new Date();

  const submittedBody = (
    <Container
      className={
        'cnd-custom-container-small my-12 px-0 px-lg-0 border-light-gray rounded-1'
      }
    >
      <div className="d-flex flex-column px-10 py-11 px-lg-11">
        <section className="d-flex justify-content-center">
          <h5 className="m-0">Submitted, You are almost done</h5>
        </section>
        <section className="d-flex justify-content-center mt-7">
          <span className="text-sub1 fw-light lh-xs">
            You still have required actions below
          </span>
        </section>
        <section className="d-flex justify-content-center mt-7">
          <img src={PuzzleImg} alt="puzzle-img" />
        </section>
        <section className="d-flex flex-column flex-lg-row justify-content-lg-between mt-11">
          <div className="d-flex justify-content-around justify-content-lg-between width-lg-50-sm-100">
            <div className="d-flex flex-column justify-content-center">
              <span className="text-sub1 fw-normal lh-xs">Submitted</span>
              <span className="text-sub1 fw-light lh-xs">
                {formatDate(initialDate, DateFormats.SHORT)}
              </span>
            </div>
            <div className="d-flex flex-column justify-content-center">
              <div className="d-flex justify-content-center">
                <span className="text-sub1 fw-normal lh-xs"> Status</span>
              </div>
              <div>
                <span className="text-light-salmon">
                  <FontAwesomeIcon
                    icon={light('circle-info')}
                    className={'me-4'}
                  />
                </span>
                <span className="text-sub1 fw-light lh-xs"> Pending</span>
              </div>
            </div>
          </div>
          <Button
            variant="primary"
            className={'order-1 order-lg-3 mt-3 mt-lg-0'}
            size="sm"
            onClick={handleShowSummaryModal}
          >
            Show Summary
          </Button>
          <BeneficiariesSummaryModal
            showSummaryModal={showSummaryModal}
            handleCloseSummaryModal={handleCloseSummaryModal}
          />
        </section>
      </div>
      <div className="d-flex flex-column px-10 py-11 px-lg-11 border-top-light-gray">
        <section className="d-flex justify-content-start">
          <h5 className="m-0">For your records</h5>
        </section>
        <section className="d-flex justify-content-start mt-7">
          <span className="fw-light text-sub1 lh-xs">
            There are multiple options for retaining a record of this
            transaction.
          </span>
        </section>
        <section className="d-flex flex-column justify-content-start mt-7">
          <Row className="m-0 d-flex justify-content-between">
            <Col className="p-0 d-flex justify-content-start align-items-center">
              <span className="fw-light text-sub1 lh-xs text-light-grey">
                PDF
              </span>
            </Col>
            <Col className="p-0 col-7 col-lg-9">
              <Link
                href={`${process.env.PUBLIC_URL}/${'blank-demo-example.pdf'}`}
                target={'_blank'}
              >
                <Button
                  leftIcon={light('file-pdf')}
                  variant="primary-outline"
                  className={'order-1 order-lg-3'}
                  size="sm"
                >
                  Download/Print
                </Button>
              </Link>
            </Col>
          </Row>
          <Row className="m-0 d-flex justify-content-between pt-3">
            <Col className="p-0 d-flex justify-content-start align-items-start">
              <span className="fw-light text-sub1 lh-xs text-light-grey">
                Standard Mail
              </span>
            </Col>
            <Col className="p-0 col-7 col-lg-9 d-flex justify-content-start align-items-start">
              <span className="fw-light text-sub1 lh-xs text-light-grey">
                You will automatically receive written confirmation of this
                transaction by U.S. Mail.
              </span>
            </Col>
          </Row>
        </section>
      </div>
      <div className="d-flex flex-column px-10 py-11 px-lg-11 bg-white solid-salmon-border rounded-border-bottom">
        <section className="d-flex justify-content-start">
          <h5 className="m-0">Action Required</h5>
        </section>
        <section className="d-flex justify-content-start mt-7">
          <span className="fw-light text-sub1 lh-xs">
            In order to complete the verification you must send in the required
            documentation
          </span>
        </section>
        <section className="mt-7">
          <div className="lh-xs pt-1 d-flex align-items-center">
            <FontAwesomeIcon
              icon={light('check')}
              className={'pe-3 text-light-grey'}
            />
            <span className="text-sub1 fw-light">
              Beneficiary Designation Authorization Form
            </span>
          </div>
          <div className="lh-xs pt-1 d-flex align-items-center">
            <FontAwesomeIcon
              icon={light('check')}
              className={'pe-3 text-light-grey'}
            />
            <span className="text-sub1 fw-light">Spousal Waiver</span>
          </div>
        </section>
        <section className="mt-7">
          <Blockquote variant={'warning-bg'} lineVariant={'warning'}>
            <span className={'fw-light text-light-grey'}>
              Note: You have 30 Days
            </span>
          </Blockquote>
        </section>
        <section className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
          <span className="fw-normal text-sub1 lh-xs d-flex align-items-center">
            STEP 1 - Download/Print Forms
          </span>
          <div>
            <Button
              leftIcon={light('mailbox')}
              variant={'danger-outline'}
              className={'order-1 order-lg-3 mt-7 mt-lg-0 me-7'}
              size={'sm'}
              onClick={handleShowReceiveMailModal}
            >
              Mail
            </Button>
            <ReceiveFormsMailModal
              showModal={showReceiveMailModal}
              closeModal={handleCloseReceiveMailModal}
            />

            <a
              href={`${process.env.PUBLIC_URL}/${'blank-demo-example.pdf'}`}
              target={'_blank'}
              rel="noreferrer"
              className="text-decoration-none"
            >
              <Button
                leftIcon={light('file-download')}
                variant={'danger-outline'}
                className={'order-1 order-lg-3 mt-7 mt-lg-0'}
                size={'sm'}
              >
                Download/Print
              </Button>
            </a>
          </div>
        </section>
        <section className="d-flex flex-column justify-content-start mt-7">
          <span className="fw-normal text-sub1 lh-xs">STEP 2 - Send</span>
          <span className="fw-light text-sub1 lh-xs">
            Submit all of the forms and copies from step 1 and upload or email
            them
          </span>
        </section>
        <section className="d-lg-flex flex-lg-row flex-sm-column">
          <div className="d-flex flex-column border-separator py-10 px-lg-11 w-100">
            <div className="d-flex justify-content-start">
              <span className="fw-light text-sub1 lh-xs">Scan and Upload</span>
            </div>
            <div className="d-flex justify-content-start">
              <Button
                leftIcon={light('upload')}
                variant="danger-outline"
                className={'order-1 order-lg-3 mt-7'}
                size="sm"
                onClick={() => openModal('document-upload')}
              >
                Upload Document
              </Button>
            </div>
          </div>
          <div className="d-flex flex-column py-10 px-lg-11 w-100">
            <span className="fw-light text-sub1 lh-xs">Mail</span>
            <div className="d-flex flex-column mt-7">
              <span className="fw-light text-sub1 lh-xs">Regular Mail</span>
              <span className="fw-light text-sub1 lh-xs">
                Interlink
              </span>
              <span className="fw-light text-sub1 lh-xs">Benefits Express</span>
              <span className="fw-light text-sub1 lh-xs">P.O. Box 5241</span>
              <span className="fw-light text-sub1 lh-xs">
                Cherry Hill, NJ 08034-5241
              </span>
            </div>
            <div className="d-flex flex-column mt-7">
              <span className="fw-light text-sub1 lh-xs">
                Overnight Delivery
              </span>
              <span className="fw-light text-sub1 lh-xs">
                Benefits Express Employee Service Center
              </span>
              <span className="fw-light text-sub1 lh-xs">
                101 Woodcrest Road
              </span>
              <span className="fw-light text-sub1 lh-xs">
                Cherry Hill, NJ 08003
              </span>
            </div>
          </div>
        </section>
        <section className="mt-7">
          <span className="fw-light text-sub1 lh-xs">Review</span>
        </section>
        <section className="d-flex flex-column">
          <span className="fw-light text-sub1 lh-xs">
            Once your completed paperwork has been received it will be reviewed.
            If there are any problems with the paperwork you will be notified...
          </span>
        </section>
      </div>
    </Container>
  );

  const successfulBody = (
    <Container
      className={
        'cnd-custom-container-small my-12 px-0 px-lg-0 border-light-gray rounded-1'
      }
    >
      <div className="d-flex flex-column px-10 py-11 px-lg-11">
        <section className="d-flex justify-content-center">
          <h5 className="m-0">Successful</h5>
        </section>
        <section className="d-flex justify-content-center mt-7">
          <span className="text-sub1 fw-light lh-xs">
            Your beneficiary has been verified
          </span>
        </section>
        <section className="d-flex justify-content-center mt-7">
          <img
            src={CompletedImg}
            alt="puzzle-img"
            style={{ maxWidth: 200, maxHeight: 220 }}
          />
        </section>
        <section className="d-flex flex-column flex-lg-row justify-content-lg-between mt-11">
          <div className="d-flex justify-content-around justify-content-lg-between width-lg-50-sm-100">
            <div className="d-flex flex-column justify-content-center">
              <span className="text-sub1 fw-normal lh-xs">Submitted</span>
              <span className="text-sub1 fw-light lh-xs">
                {formatDate(new Date(), DateFormats.MONTH_YEAR)}
              </span>
            </div>
            <div className="d-flex flex-column justify-content-center">
              <div className="d-flex justify-content-center">
                <span className="text-sub1 fw-normal lh-xs">Status</span>
              </div>
              <div>
                <FontAwesomeIcon
                  icon={light('check')}
                  className={'fill-marks--green pe-3'}
                />
                <span className="text-sub1 fw-light lh-xs">Completed</span>
              </div>
            </div>
          </div>
          <Button
            onClick={handleShowBeneficiarySummaryModal}
            variant="primary"
            className={'order-1 order-lg-3 mt-3 mt-lg-0'}
            size="sm"
          >
            Show Summary
          </Button>

          <BeneficiariesSummaryModal
            showSummaryModal={showBeneficiarySummaryModal}
            handleCloseSummaryModal={handleCloseBeneficiarySummaryModal}
          />
        </section>
      </div>
      <div className="d-flex flex-column px-10 py-11 px-lg-11 border-top-light-gray">
        <section className="d-flex justify-content-start">
          <h5 className="m-0">For your records</h5>
        </section>
        <section className="d-flex justify-content-start mt-7">
          <span className="fw-light text-sub1 lh-xs">
            There are multiple options for retaining a record of this
            transaction.
          </span>
        </section>
        <section className="d-flex flex-column justify-content-start mt-7">
          <Row className="m-0 d-flex justify-content-between">
            <Col className="p-0 d-flex justify-content-start align-items-start">
              <span className="fw-light text-sub1 lh-xs text-light-grey">
                PDF
              </span>
            </Col>
            <Col className="p-0 col-7 col-lg-9">
              <Link
                href={`${process.env.PUBLIC_URL}/${'blank-demo-example.pdf'}`}
                target={'_blank'}
              >
                <Button
                  leftIcon={light('file-pdf')}
                  variant="primary-outline"
                  className={'order-1 order-lg-3'}
                  size="sm"
                >
                  Download/Print
                </Button>
              </Link>
            </Col>
          </Row>

          <Row className="m-0 d-flex justify-content-between pt-3">
            <Col className="p-0 d-flex justify-content-start align-items-start">
              <span className="fw-light text-sub1 lh-xs text-light-grey">
                Standard Mail
              </span>
            </Col>
            <Col className="p-0 col-7 col-lg-9 d-flex justify-content-start align-items-start">
              <span className="fw-light text-sub1 lh-xs text-light-grey">
                You will automatically receive written confirmation of this
                transaction by U.S. Mail.
              </span>
            </Col>
          </Row>
        </section>
      </div>
      <div className="d-flex flex-column border-top px-10 py-11 px-lg-11 bg-white rounded-border-bottom">
        <section className="d-flex justify-content-start">
          <h5 className="m-0">Things to consider</h5>
        </section>
        <section className="d-flex justify-content-start mt-7">
          <span className="fw-light text-sub1 lh-xs">
            Now that you've setup your savings, you might want to consider the
            following.
          </span>
        </section>
        <section className="mt-7">
          <div className="lh-xs pt-1 d-flex">
            <FontAwesomeIcon
              icon={light('check')}
              className={'pe-3 text-light-grey'}
            />
            <span className="text-sub1 fw-light">
              Make sure to{' '}
              <span className={'d-inline-block'}>
                <Link
                  href={`#${PROFILE_ROUTE.slice(0, 9)}beneficiaries`}
                  onClick={onClick}
                  isBlue
                  className="text-primary fw-bold"
                >
                  setup your beneficiaries
                </Link>
              </span>{' '}
              for your 401(k) Savings plan.
            </span>
          </div>
          <div className="lh-xs pt-1 d-flex">
            <FontAwesomeIcon
              icon={light('check')}
              className={'pe-3 text-light-grey'}
            />
            <span className="text-sub1 fw-light">
              Check the{' '}
              <span className={'d-inline-block'}>
                <Link
                  href={`#${RETIREMENT_PLANNING_ROUTE}`}
                  isBlue
                  className="me-1 text-primary fw-bold"
                >
                  retirement planner
                </Link>
              </span>
              to see how you can save enough money for retirement.
            </span>
          </div>
          <div className="lh-xs pt-1 d-flex">
            <FontAwesomeIcon
              icon={light('check')}
              className={'pe-3 text-light-grey'}
            />
            <span className="text-sub1 fw-light">
              Review your{' '}
              <span className={'d-inline-block'}>
                <Link
                  href={`#${PROFILE_ROUTE.slice(0, 9)}communication`}
                  isBlue
                  className="me-1 text-primary fw-bold"
                >
                  communication preferences
                </Link>
              </span>
              so you'll receive an email before each increase on your
              contributions.
            </span>
          </div>
        </section>
      </div>
    </Container>
  );

  return spouseSelected ? successfulBody : submittedBody;
};

export default SubmittedSuccessful;
